import { useEffect, useState } from "react"

/**
 * Hook that detects if the user is currently typing in a programmatic editor element.
 * This is used to disable hotkeys while typing in code editors like Monaco.

 * @returns true if the active element has role="textbox", false otherwise
 */
export default function useIsTypingInEditor(): boolean {
    const [isTypingInEditor, setIsTypingInEditor] = useState(false)

    useEffect(() => {
        const checkActiveElement = () => {
            const activeElement = document.activeElement

            if (!activeElement) {
                setIsTypingInEditor(false)
                return
            }

            // Check if the element or any parent has role="textbox" (Monaco editor)
            let element: Element | null = activeElement
            while (element) {
                const role = element.getAttribute("role")

                if (role === "textbox") {
                    setIsTypingInEditor(true)
                    return
                }

                element = element.parentElement
            }

            setIsTypingInEditor(false)
        }

        // Check immediately
        checkActiveElement()

        // Listen for focus changes
        document.addEventListener("focusin", checkActiveElement)
        document.addEventListener("focusout", checkActiveElement)

        return () => {
            document.removeEventListener("focusin", checkActiveElement)
            document.removeEventListener("focusout", checkActiveElement)
        }
    }, [])

    return isTypingInEditor
}
